import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import { getAuth } from "firebase/auth"
import { child, get, getDatabase, ref } from "firebase/database"
import fbConf from "../FirebaseConf"
import Modal from "../components/Modal"
import Button from "../components/Button"
import LoaderDots from "../components/LoaderDots"
import WhiteSpace from "../components/Whitespace"
import Icon from "../Icon"
import { BREAKPOINTS } from "../GlobalTheme"
import VoltieUser from "../types/VoltieUser"
import { normalizeUser } from "../pages/Users/userHelpers"
import { CardTitle, IconButton, MutedValue } from "../pages/Users/userStyles"
import UserNotifications from "../pages/Users/UserNotifications"
import { useNotification } from "./Notification"
import { isPasskeyAvailable, isPasskeyCancelled, registerPasskey } from "../utils/passkey"

// The signed-in user's own settings (VLT-2637): the sections of the admin /users settings sheet, one per
// menu item, for every user, plus passkey setup and signing out.

const MENU = [
  { id: 'notifications', labelKey: 'user_pushTitle' },
  { id: 'calculations', labelKey: 'user_calcTitle' },
  { id: 'passkeys', labelKey: 'app_passkey' },
  { id: 'language', labelKey: 'user_language' },
] as const

type MenuId = typeof MENU[number]['id']

const Layout = styled.div`
  display: flex;
  width: min(900px, calc(100vw - 48px));
  height: min(720px, calc(100dvh - 48px));

  @media (max-width: ${BREAKPOINTS.shell}px) {
    flex-direction: column;
    width: calc(100vw - 24px);
    height: calc(100dvh - 24px);
  }
`

const SideMenu = styled.div`
  width: 220px;
  flex-shrink: 0;
  padding: 28px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-right: 1px solid ${props => props.theme.colors.gray_200};

  /* Below the shell breakpoint there is no room for a column, so the menu becomes a scrolling strip. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: auto;
    flex-direction: row;
    overflow-x: auto;
    padding: 12px;
    border-right: none;
    border-bottom: 1px solid ${props => props.theme.colors.gray_200};
  }
`

const SideMenuItem = styled.button<{ $selected: boolean }>`
  text-align: left;
  white-space: nowrap;
  padding: 10px 14px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${props => props.$selected ? 700 : 500};
  color: ${props => props.$selected ? props.theme.colors.primary : props.theme.colors.gray_700};
  background-color: ${props => props.$selected ? props.theme.colors.primaryLight : 'transparent'};
`

// Pinned to the foot of the menu column, or to the end of the strip on narrow screens.
const SignOutButton = styled.button`
  margin-top: auto;
  white-space: nowrap;
  padding: 10px 14px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.theme.colors.white};
  background-color: ${props => props.theme.colors.danger};

  &:hover {
    background-color: ${props => props.theme.colors.dangerActive};
  }

  @media (max-width: ${BREAKPOINTS.shell}px) {
    margin-top: 0px;
    margin-left: auto;
  }
`

const Content = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 28px 32px;

  @media (max-width: ${BREAKPOINTS.phone}px) {
    padding: 20px 16px;
  }
`

const CloseButtonContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
`

function PasskeyPanel() {
  const { t } = useTranslation()
  const { addNotification } = useNotification()
  const [isAdding, setIsAdding] = useState<boolean>(false)

  async function handleAddPasskey() {
    const user = getAuth(fbConf).currentUser
    if (!user || isAdding) return
    setIsAdding(true)
    try {
      await registerPasskey(user)
      addNotification(t('app_passkeyAdded'), 'TEXT')
    } catch (err) {
      if (!isPasskeyCancelled(err)) {
        console.log('Unable to add a passkey', err)
        addNotification(t('app_couldntCreateThePasskeyPleaseTryAgain'), 'TEXT', 'error')
      }
    }
    setIsAdding(false)
  }

  return (
    <>
      <CardTitle>{t('app_passkey')}</CardTitle>
      <MutedValue>{t('app_createAPasskeyToSignInNextTimeWithYourFingerprintFaceOrScreenLockNoPasswordNeeded')}</MutedValue>
      <WhiteSpace height="20px" />
      {isPasskeyAvailable()
        ? <Button onclick={handleAddPasskey} label={isAdding ? <LoaderDots /> : t('app_addPasskey')} width="max(12vw, 160px)" />
        : <MutedValue>{t('app_passkeysAreNotAvailableInThisBrowser')}</MutedValue>}
    </>
  )
}

export default function ProfileModal({ isAdmin, onClose, onSignOut }: { isAdmin: boolean, onClose: () => void, onSignOut: () => void }) {
  const { t } = useTranslation()
  const uid = getAuth(fbConf).currentUser?.uid ?? ''

  const [user, setUser] = useState<VoltieUser | null>()
  const [page, setPage] = useState<MenuId>(MENU[0].id)

  async function fetchUser() {
    if (!uid) {
      setUser(null)
      return
    }
    try {
      const snap = await get(child(ref(getDatabase(fbConf)), `voltie/chargerusers/${uid}`))
      const val = snap.val()
      setUser(val ? normalizeUser(val) : null)
    } catch (e) {
      console.log(e)
      setUser(null)
    }
  }

  useEffect(() => {
    fetchUser()
  }, [uid])

  return (
    <Modal onEscape={onClose}>
      <Layout role="dialog" aria-modal="true" aria-label={t('app_profile')}>
        <SideMenu role="tablist" aria-orientation="vertical">
          {MENU.map(item => (
            <SideMenuItem key={item.id} role="tab" aria-selected={page === item.id} $selected={page === item.id} onClick={() => setPage(item.id)}>
              {t(item.labelKey)}
            </SideMenuItem>
          ))}
          <SignOutButton onClick={onSignOut}>{t('app_signout')}</SignOutButton>
        </SideMenu>
        <Content role="tabpanel">
          <CloseButtonContainer>
            <IconButton aria-label={t('charger_close')} onClick={onClose}>
              <Icon path="general.x" width="20px" height="20px" />
            </IconButton>
          </CloseButtonContainer>
          {page === 'passkeys' ? <PasskeyPanel /> : <>
            {user === undefined && <div style={{ textAlign: 'center', marginTop: '80px' }}><LoaderDots /></div>}
            {user === null && <p style={{ textAlign: 'center', marginTop: '80px', fontWeight: 500 }}>{t('user_notFound')}</p>}
            {user && <UserNotifications user={user} uid={uid} isAdmin={isAdmin} refresh={fetchUser} section={page} />}
          </>}
        </Content>
      </Layout>
    </Modal>
  )
}
